import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, FormEvent, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import { FormLayout, Label, Larger } from '@/components/Forms';
import VideoField from '@/components/Forms/Fields/VideoField';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Switch from '@/components/Switch';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useDidUpdate from '@/hooks/useDidUpdate';
import {
  ChannelGroupResource,
  ChannelResource,
  EmployeeResource,
  ReleasePredictionResource,
  ReleaseResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { productionTeams } from '@/utils/data';
import { compilationGroups } from '@/utils/data/distribution';
import { ReleaseCompilationTypeEnum, ReleaseStatusEnum } from '@/utils/enums';
import {
  handleFormErrors,
  stringsArrayToDropdownOptions,
} from '@/utils/helpers';

import ChannelsList from './ChannelsList';
import DistributionScheduleStatusModal from './DistributionScheduleStatusModal';
import FormChannelGroupSwitch from './FormChannelGroupSwitch';
import ReelsChannelsList from './ReelsChannelsList';
import ReelsCounter from './ReelsCounter';
import VideoReelSwitchField from './VideoReelSwitchField';

const CheckboxHolder = styled.div`
  margin-top: -8px;
`;

const Checkmark = styled.span`
  width: 16px;
  height: 16px;
  top: 0;
  bottom: 0;
  left: 0;
  margin: auto 0;
  position: absolute;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 1px;
  transition: 0.2s ease-out all;
  &::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: none;
  }
`;

const Input = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 1px;
  width: 1px;
  &:checked ~ ${Checkmark} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: ${({ theme }) => theme.colors.purple};
    &::after {
      display: block;
    }
  }
`;

export const StyledLabel = styled(Label)`
  position: relative;
  cursor: pointer;
  user-select: none;
  margin: 0;
  padding-left: 25px;
`;

export const compilationTypes = [
  {
    value: ReleaseCompilationTypeEnum.FRESH,
    label: 'Fresh',
  },
  {
    value: ReleaseCompilationTypeEnum.DISTRIBUTION,
    label: 'Distibution',
  },
  {
    value: ReleaseCompilationTypeEnum.MODIFIED_REDISTRIBUTION,
    label: 'Modified Distribution',
  },
  {
    value: ReleaseCompilationTypeEnum.REUSE,
    label: 'Reuse',
  },
  {
    value: ReleaseCompilationTypeEnum.REDISTRIBUTION,
    label: 'Redistribution',
  },
  {
    value: ReleaseCompilationTypeEnum.REDISTRIBUTION_PREDICTED,
    label: 'Redistribution / Predicted',
  },
];

const compilationGroupOptions =
  stringsArrayToDropdownOptions(compilationGroups);

export const AddButtonHolder = styled.div`
  margin-top: 24px;
  svg {
    margin-right: 8px;
  }
  button {
    width: 100%;
  }
`;

const StyledSwitch = styled((props) => <Switch<FormDataT> {...props} />)`
  margin-left: 20px;
  flex-shrink: 0;
`;

const InfoBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  padding: 16px 12px;
  display: flex;
`;

const StyledText = styled(Text)`
  margin-bottom: 4px;
`;

const AddChannelButton = styled.button`
  color: ${({ theme }) => theme.colors.purple};
  display: inline-flex;
  align-items: center;
  margin-top: 16px;
  svg {
    margin-right: 8px;
  }
`;

const RemoveChannelButton = styled(RemoveButton)`
  margin: 0;
  position: absolute;
  top: -10px;
  right: 0;
`;

const ChannelsFieldGroup = styled(Larger)`
  position: relative;
`;

const prefillableEditors = [
  50, //'Abel Derick',
  2, //'Artsiom Skrynik',
  21, //'Monika Šarlauskaitė',
  69, //'Albert Paci',
  1, //'Arnob Debnath',
  3, //'Natalia Rumyantseva',
  4, //'Victor Russo',
  78, //'Johnny T. Chalatsis',
  28, //'Kinga Maksym',
  13, //'Osvaldas Butkus',
];

export type FormDataT = {
  video?: string;
  external_project_id?: string;
  thumbnail?: string;
  description?: string;
  notes: string;
  released_at?: string;
  use_watermark: boolean;
  is_reserved: boolean;
  watermark_format: string;
  is_exclusive: number;
  is_reel: number;
  title?: string;
  channels: Array<{
    id?: number;
  }>;
  channel_id?: number;
  compilation_type?: number;
  compilation_group?: string;
  part?: number;
  production_team?: string;
  fresh_id?: string;
  director?: number;
  editor?: number;
  publisher?: number;
  archivist?: number;
  channel_group?: number;
};

interface Props {
  onClose: () => void;
  editableRelease?: ReleaseResource;
  prediction?: ReleasePredictionResource;
  day?: DateTime;
}

const DistributionScheduleModal: FC<Props> = ({
  onClose,
  day,
  editableRelease,
  prediction,
}) => {
  const alerts = useAlerts();

  const [showStatusModal, setShowStatusModal] = useState(false);

  const [isInstant, setIsInstant] = useState(false);

  const [isChannelGroups, setIsChannelGroups] = useState(
    !!editableRelease?.channelGroup ? true : false,
  );

  const queryClient = useQueryClient();

  const { data: channelOptions } = useQuery(
    ['channels', { onlyBp: true, authorized: true }],
    () =>
      api.get<never, ChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          only_bp: 1,
        },
      }),
    {
      staleTime: 1000 * 60,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    },
  );

  const { data: channelGroups } = useQuery(
    ['channel_groups'],
    () =>
      api.get<never, ChannelGroupResource[]>(`/v1/distribution/channel-groups`),
    {
      staleTime: 1000 * 60,
    },
  );

  const { data: employees } = useQuery(
    ['employees'],
    () => api.get<never, EmployeeResource[]>('/v1/employees'),
    {
      staleTime: 120 * 1000,
      select: (data) => {
        const mapFn = (opt: EmployeeResource) => ({
          label: opt.name,
          value: opt.id,
        });

        const filterFn = (opt: EmployeeResource, roleTitle: string) =>
          opt.roles!.map((r) => r.title).includes(roleTitle);

        return {
          directors: data.filter((e) => filterFn(e, 'Director')).map(mapFn),
          editors: data.filter((e) => filterFn(e, 'Editor')).map(mapFn),
          publishers: data.filter((e) => filterFn(e, 'Publisher')).map(mapFn),
          archivists: data.filter((e) => filterFn(e, 'Archivist')).map(mapFn),
        };
      },
    },
  );

  const {
    control,
    handleSubmit,
    setError,
    resetField,
    clearErrors,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<FormDataT>({
    defaultValues: {
      released_at: editableRelease?.releasedAt ?? day?.toISO() ?? '',
      use_watermark: editableRelease?.useWatermark ?? false,
      is_reserved: editableRelease?.isReserved ?? false,
      watermark_format: editableRelease?.properties?.watermark_format ?? '1:1',
      channel_group:
        editableRelease?.channelGroup && editableRelease?.channelGroup.id,
      channels: [
        prediction?.channel
          ? {
              id: prediction.channel.id,
            }
          : {},
      ],
      channel_id: editableRelease?.channel?.id,
      title: editableRelease?.title ?? prediction?.title ?? '',
      description:
        editableRelease?.description ?? prediction?.description ?? '',
      notes: editableRelease?.notes ?? '',
      compilation_type: prediction
        ? ReleaseCompilationTypeEnum.REDISTRIBUTION_PREDICTED
        : editableRelease?.compilationType,
      compilation_group: editableRelease?.compilationGroup,
      part: editableRelease?.part ?? prediction?.part,
      production_team:
        editableRelease?.productionTeam ?? prediction?.productionTeam,
      fresh_id: editableRelease?.freshId ?? prediction?.freshId ?? '',
      external_project_id: editableRelease?.projectId ?? prediction?.projectId,
      director:
        editableRelease?.employees?.find((e) => e.release_role === 'director')
          ?.id ?? prediction?.director,
      editor:
        editableRelease?.employees?.find((e) => e.release_role === 'editor')
          ?.id ?? prediction?.editor,
      publisher:
        editableRelease?.employees?.find((e) => e.release_role === 'publisher')
          ?.id ?? prediction?.publisher,
      is_exclusive: editableRelease?.isExclusive ? 1 : 0,
      is_reel: editableRelease?.isReel ? 1 : 0,
      archivist:
        editableRelease?.employees?.find((e) => e.release_role === 'archivist')
          ?.id ?? prediction?.archivist,
    },
  });

  const {
    fields: channelFields,
    append: appendChannel,
    remove: removeChannel,
  } = useFieldArray({
    control,
    name: 'channels',
  });

  const onSubmit = (
    status: ReleaseStatusEnum | null,
    e?: FormEvent<HTMLFormElement>,
  ) => {
    clearErrors();
    return handleSubmit(
      async ({ channel_group, channels, released_at, ...data }) => {
        try {
          const body = {
            ...data,
            ...(!!released_at && { released_at }),
            ...(!!status && { status }),
            ...(!isChannelGroups &&
              (channels.length > 1 || !!channels[0].id) && {
                channels,
              }),
            ...(isChannelGroups && {
              channel_group,
            }),
            ...(!!prediction && {
              release_prediction_id: prediction.id,
            }),
            is_3rd_party: editableRelease?.is3rdParty ? 1 : 0,
            is_instant: isInstant ? 1 : 0,
          };

          await api({
            url: editableRelease
              ? `/v1/releases/${editableRelease.id}`
              : '/v1/releases',
            method: editableRelease ? 'put' : 'post',
            data: body,
          });

          queryClient.invalidateQueries(['releases'], {
            exact: false,
          });

          if (prediction) {
            await queryClient.invalidateQueries(['release_predictions']);
          }

          alerts.success(`Schedule have been successfully created`);
          onClose();
        } catch (err) {
          handleFormErrors<FormDataT>(err, setError, alerts, true);
        }
      },
    )(e);
  };

  const useWatermark = watch('use_watermark');

  const isReel = watch('is_reel');

  const editor = watch('editor');

  const channelGroup = watch('channel_group');

  useDidUpdate(() => {
    if (!isReel) {
      return;
    }

    setValue('compilation_group', 'Reels');
    setValue('thumbnail', '');
  }, [isReel, setValue]);

  useDidUpdate(() => {
    if (isInstant) {
      resetField('released_at');
    }
  }, [isInstant]);

  useDidUpdate(() => {
    if (!editor) {
      return;
    }

    if (!prefillableEditors.includes(editor)) {
      return;
    }

    setValue('compilation_group', '1-Minute');
    setValue('compilation_type', ReleaseCompilationTypeEnum.REUSE);
    setValue('part', 1);
  }, [editor]);

  return (
    <Modal>
      <SidePanel
        heading={editableRelease ? 'Edit schedule' : 'New schedule'}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={(e) => onSubmit(null, e)} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <VideoReelSwitchField control={control} />
              </Larger>
              <Larger>
                <VideoField<FormDataT>
                  name="video"
                  control={control}
                  label="Video"
                  existingMedia={editableRelease?.video}
                  notes={
                    <>
                      Format: MP4
                      <br />
                      Max file size: 1GB
                    </>
                  }
                />
              </Larger>
              {!isReel && (
                <FormField
                  control={control}
                  name="thumbnail"
                  existingMedia={editableRelease?.thumbnail}
                  type="image"
                  label="Thumbnail"
                  id="thumbnailInput"
                  notes={
                    <>
                      JPG, PNG, GIF up to 10MB
                      <br />
                      Recommended dimensions: 1200x675
                    </>
                  }
                />
              )}
              <Larger>
                <InfoBox>
                  <div>
                    <StyledText $type="smallBold">Watermark</StyledText>
                    <Text $color="gray" $type="smallMedium">
                      Assigned watermark will be applied on video from
                      Distribution settings.
                    </Text>
                  </div>
                  <StyledSwitch name="use_watermark" control={control} />
                </InfoBox>
              </Larger>
              {!!useWatermark && (
                <FormField<FormDataT, string>
                  control={control}
                  options={[
                    {
                      value: '1:1',
                      label: '1:1 (1080x1080)',
                    },
                    {
                      value: '4:5',
                      label: '4:5 (1728x2160)',
                    },
                    {
                      value: '9:16',
                      label: '9:16 (1080x1920)',
                    },
                  ]}
                  name="watermark_format"
                  label="Watermark Format"
                  type="select"
                />
              )}
              <FormField<FormDataT, string>
                control={control}
                options={productionTeams.map((t) => ({
                  label: t,
                  value: t,
                }))}
                name="production_team"
                label="Production Team"
                type="select"
              />
              <Larger>
                <FormChannelGroupSwitch
                  isChannelGroups={isChannelGroups}
                  setIsChannelGroups={setIsChannelGroups}
                />
              </Larger>
              <Larger>
                {!!isChannelGroups ? (
                  <>
                    <FormField<FormDataT, number>
                      control={control}
                      type="select"
                      name="channel_group"
                      label="Channel Groups"
                      options={
                        channelGroups?.map((group) => {
                          return {
                            label: group.name,
                            value: group.id,
                          };
                        }) ?? []
                      }
                    />
                    {!!channelGroup && !!channelGroups?.length && (
                      <>
                        {isReel ? (
                          <ReelsChannelsList
                            currentGroup={channelGroups.find(
                              (group) => group.id === channelGroup,
                            )}
                          />
                        ) : (
                          <ChannelsList
                            currentGroup={channelGroups.find(
                              (group) => group.id === channelGroup,
                            )}
                          />
                        )}
                      </>
                    )}
                  </>
                ) : editableRelease ? (
                  <>
                    <FormField<FormDataT, number>
                      control={control}
                      name="channel_id"
                      label="Channel"
                      type="select"
                      options={channelOptions ?? []}
                    />
                    <ReelsCounter control={control} name={`channel_id`} />
                  </>
                ) : (
                  <>
                    <FormLayout>
                      {channelFields.map((field, index) => (
                        <ChannelsFieldGroup key={field.id}>
                          <FormField<FormDataT, number>
                            control={control}
                            name={`channels.${index}.id`}
                            label={
                              index === 0 ? 'Channel' : `Channel (${index + 1})`
                            }
                            type="select"
                            options={channelOptions ?? []}
                          />
                          <ReelsCounter
                            control={control}
                            name={`channels.${index}.id`}
                          />
                          {channelFields.length > 1 && (
                            <RemoveChannelButton
                              type="button"
                              onClick={() => removeChannel(index)}
                            >
                              Remove
                            </RemoveChannelButton>
                          )}
                          {!!errors.channels &&
                            index === channelFields.length - 1 && (
                              <ValidationMessage>
                                {errors.channels.message}
                              </ValidationMessage>
                            )}
                        </ChannelsFieldGroup>
                      ))}
                    </FormLayout>
                    <AddChannelButton
                      type="button"
                      onClick={() => appendChannel({})}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="8"
                          fill="currentColor"
                          fillOpacity="0.12"
                        />
                        <path
                          d="M8 5V11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5 8H11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <Text as="span" $type="smallHeadline">
                        Add additional channel
                      </Text>
                    </AddChannelButton>
                  </>
                )}
              </Larger>
              <FormField<FormDataT>
                control={control}
                name="released_at"
                label="Date & Time"
                type="datepicker"
                disabled={isInstant}
              />
              <Larger>
                <CheckboxHolder>
                  <StyledLabel>
                    Instant post
                    <Input
                      type="checkbox"
                      checked={isInstant}
                      onChange={() => {
                        setIsInstant((prev) => !prev);
                      }}
                    />
                    <Checkmark />
                  </StyledLabel>
                </CheckboxHolder>
              </Larger>
              <FormField<FormDataT>
                control={control}
                name="title"
                placeholder="Add title"
                label="Title"
              />
              <FormField<FormDataT>
                control={control}
                name="description"
                label="Description"
                type="textarea"
              />
              <FormField<FormDataT>
                control={control}
                name="notes"
                label="Note"
                subLabel="(Internal Information)"
                type="textarea"
              />
              <FormField<FormDataT>
                control={control}
                name="external_project_id"
                placeholder="Add ID"
                label="Project ID"
              />
              <FormField<FormDataT>
                control={control}
                name="fresh_id"
                placeholder="Add ID"
                label="Fresh ID (optional)"
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="director"
                label="Director"
                options={employees?.directors ?? []}
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="editor"
                label="Editor"
                options={employees?.editors ?? []}
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="publisher"
                label="Publisher"
                options={employees?.publishers ?? []}
              />
              {!!editableRelease?.is3rdParty && (
                <FormField<FormDataT, number>
                  control={control}
                  type="select"
                  name="is_exclusive"
                  label="Exclusivity"
                  options={[
                    {
                      label: 'Exclusive',
                      value: 1,
                    },
                    {
                      label: 'Non-Exclusive',
                      value: 0,
                    },
                  ]}
                />
              )}
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="archivist"
                label="Archivist"
                options={employees?.archivists ?? []}
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="part"
                label="Part"
                options={Array.from(Array(10).keys()).map((part) => ({
                  value: part,
                  label: part.toString(),
                }))}
                menuPlacement="top"
              />
              <FormField<FormDataT, string>
                control={control}
                type="select"
                name="compilation_group"
                label="Compilation Group"
                options={compilationGroupOptions}
                menuPlacement="top"
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="compilation_type"
                label="Compilation type"
                options={compilationTypes}
                menuPlacement="top"
              />
              <Larger>
                <InfoBox>
                  <div>
                    <StyledText $type="smallBold">Reserve</StyledText>
                    <Text $color="gray" $type="smallMedium">
                      Videos been made in a advance to be prepared for any
                      unplanned changes in workflow
                    </Text>
                  </div>
                  <StyledSwitch name="is_reserved" control={control} />
                </InfoBox>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            {isInstant ? (
              <Button
                type="button"
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={() => onSubmit(ReleaseStatusEnum.WAITING)}
              >
                Post
              </Button>
            ) : (
              <Button type="button" onClick={() => setShowStatusModal(true)}>
                Move to
              </Button>
            )}
            <Button
              color="secondary"
              type="button"
              onClick={() => onSubmit(editableRelease!.status)}
              loading={isSubmitting && !showStatusModal}
              disabled={!editableRelease || isSubmitting}
            >
              Save
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
      <AnimatePresence>
        {showStatusModal && (
          <DistributionScheduleStatusModal
            onClose={() => setShowStatusModal(false)}
            isSubmitting={isSubmitting}
            onSubmit={onSubmit}
          />
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default DistributionScheduleModal;
